import * as Sentry from '@sentry/react';

export enum SENTRY_CONTEXT_KEYS {
  WIDGET = 'widget',
  SENDER = 'sender',
}

// Span configuration constants
export const SENTRY_SPANS = {
  WIDGET_INIT: {
    name: 'Chat Widget Complete Initialization',
    op: 'task.widget.init',
    attributes: {
      'widget.environment': import.meta.env.VITE_ENV || 'unknown',
    },
  },
  CSS_LOAD: {
    name: 'Load Widget CSS',
    op: 'resource.css',
  },
  DOM_CREATE: {
    name: 'Create Widget DOM',
    op: 'ui.dom.create',
  },
  REACT_MOUNT: {
    name: 'Mount React App',
    op: 'ui.react.mount',
  },
} as const;

// Sentry status codes for consistency
export const SENTRY_STATUS = {
  OK: { code: 1 },
  ERROR: { code: 2 },
} as const;

// Helper functions to reduce duplication
export const handleSpanSuccess = (span?: Sentry.Span) => {
  span?.setStatus(SENTRY_STATUS.OK);
};

export const handleSpanError = (span?: Sentry.Span, error?: Error) => {
  span?.setStatus(SENTRY_STATUS.ERROR);
  if (error) {
    span?.recordException(error);
  }
};

// Advanced wrapper for spans with automatic error handling and custom error messages
export const executeWithSentrySpan = async <T>(
  spanConfig: (typeof SENTRY_SPANS)[keyof typeof SENTRY_SPANS],
  operation: (span?: Sentry.Span) => Promise<T> | T,
  options?: {
    customErrorMessage?: string;
    shouldThrow?: boolean;
  },
): Promise<T | undefined> => {
  return Sentry.startSpan(spanConfig, async (span) => {
    try {
      const result = await operation(span);
      handleSpanSuccess(span);
      return result;
    } catch (error) {
      handleSpanError(span, error as Error);
      if (options?.customErrorMessage) {
        console.error(options.customErrorMessage, error);
      }
      if (options?.shouldThrow !== false) {
        throw error;
      }
      return undefined;
    }
  });
};

const initSentry = () => {
  Sentry.init({
    dsn: 'https://78ac4b79c084e96ae78121e6ba400671@o983280.ingest.us.sentry.io/4509822826577921',
    sendDefaultPii: true,
    environment: import.meta.env.VITE_ENV,
    integrations: [
      Sentry.captureConsoleIntegration({
        levels: ['error'],
      }),
      Sentry.httpClientIntegration(),
      Sentry.browserTracingIntegration({
        // Widget-specific configuration
        instrumentNavigation: false, // Widget doesn't use traditional navigation
        instrumentPageLoad: true,
      }),
    ],
    tracesSampleRate: import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE,
    // Add performance monitoring for specific operations
    beforeSendTransaction(event) {
      // Add widget-specific context
      event.contexts = {
        ...event.contexts,
        widget: {
          environment: import.meta.env.VITE_ENV || 'unknown',
        },
      };

      return event;
    },
    // Enhanced error handling
    beforeSend(event) {
      // Add widget context to error events
      event.tags = {
        ...event.tags,
        component: 'widget',
      };

      return event;
    },
  });
};

export {
  reactErrorHandler,
  ErrorBoundary,
  setContext as setSentryContext,
  captureException as sentryCaptureException,
  startSpan as sentryStartSpan,
} from '@sentry/react';

export default initSentry;
